import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ChargingKiosk.scss';
import './ChargingKiosk.modal.scss';
import { Player } from '@lottiefiles/react-lottie-player';
import evCharging from 'media/lottie_files/ev-charging.json';
import evChargerStation from 'media/lottie_files/ev_charger_station.json';
import { PlayIcon, StopCircleIcon } from '@shopify/polaris-icons';
import { socket } from './socket';
import {
  ConnectorStatus,
  helper_fmtNumber,
  helper_fmtTime,
  useStartTransaction,
  useStopTransaction,
  type ChargePoint,
  type ChargeStationConnector,
  type ConnectorWithLatestSessionDTO,
  type OcppConnectorStatus,
  type SessionMetricsDTO,
  type VehicleDTO,
} from 'queries/charge_station.query';
import { BlockStack, Box, Button, Card, DescriptionList, Grid, InlineGrid, InlineStack, LegacyCard, Link, ProgressBar, Text } from '@shopify/polaris';
import { useNotification } from 'context/NotificationContext';
import { useNavigate } from 'react-router-dom';

type Props = {
  station?: ChargePoint;
  connectors?: ConnectorWithLatestSessionDTO[];
  stationName?: string;
  stationId?: string; // dùng làm cpid
  vendor?: string;
  model?: string;
};

type ApprovalDecision = 'allow' | 'deny';

type ApprovalRequest = {
  approvalId: string;
  cpid: string;
  idTag: string;
  connectorId: number;
  at: string;
  expiresAt?: string;
};

const normId = (v: any) =>
  String(v || '')
    .trim()
    .toLowerCase();

function StatusPill({ status }: { status: keyof typeof ConnectorStatus }) {
  const tone =
    status === 'Charging'
      ? 'ok'
      : status === 'Available'
        ? 'info'
        : status === 'Preparing' || status === 'Finishing'
          ? 'warn'
          : status === 'Faulted'
            ? 'bad'
            : 'muted';

  return <span className={`pill pill-${tone}`}>{status}</span>;
}

function SocRing({ soc }: { soc: number | null }) {
  const size = 98;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = soc == null ? 0 : Math.max(0, Math.min(100, soc));
  const dash = (p / 100) * c;

  return (
    <div className="soc">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="soc-bg" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle className="soc-fg" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} strokeDasharray={`${dash} ${c - dash}`} />
      </svg>
      <div className="soc-text">
        <div className="soc-val">{soc == null ? '—' : `${Math.round(p)}%`}</div>
      </div>
    </div>
  );
}

function GunCard({ data, isLive, onStart, onEnd }: { data: ConnectorWithLatestSessionDTO; isLive: boolean; onStart: () => void; onEnd: () => void }) {
  /** Start oái oămmmm lắm, nó thông báo charging */
  const canStart = isLive && data?.connector?.status === 'Preparing';
  const canEnd = isLive && data?.connector?.status === 'Charging';
  const isCharging = data?.connector?.status === 'Charging';
  const history = useNavigate();

  return (
    <section
      className={`gun-card ${data?.connector?.status === 'Charging' ? 'is-charging' : ''} ${data?.connector?.status === 'Faulted' ? 'is-fault' : ''}`}
    >
      <header className="gun-head">
        <div className="gun-title">
          <StatusPill status={data?.connector?.status} />
        </div>
        <div className="gun-title">
          <div className="gun-sub">Connector #{data?.connector?.connectorId}</div>
        </div>

        <div className="gun-right">
          <div className="btn-row">
            <Button icon={PlayIcon} variant="plain" onClick={onStart} disabled={!canStart}>
              Bắt đầu sạc
            </Button>
          </div>
        </div>
      </header>

      <br />

      <div className="gun-main">
        <SocRing soc={isCharging ? data?.latestSession?.metrics?.socEnd || data?.latestSession?.metrics?.socStart : 0} />

        <div className="metrics">
          <div className="metric">
            <div className="k">Power</div>
            <div className="v">
              {isCharging ? helper_fmtNumber(data?.latestSession?.metrics?.powerKw, 1) : '--'} <span className="u">kW</span>
            </div>
          </div>

          <div className="metric">
            <div className="k">Energy</div>
            <div className="v">
              {isCharging ? helper_fmtNumber(data?.latestSession?.metrics?.energyKwh, 3) : '--'} <span className="u">kWh</span>
            </div>
          </div>

          <div className="metric">
            <div className="k">Voltage</div>
            <div className="v">
              {isCharging ? helper_fmtNumber(data?.latestSession?.metrics?.voltageV, 0) : '--'} <span className="u">V</span>
            </div>
          </div>

          <div className="metric">
            <div className="k">Current</div>
            <div className="v">
              {isCharging ? helper_fmtNumber(data?.latestSession?.metrics?.currentA, 0) : '--'} <span className="u">A</span>
            </div>
          </div>

          <div className="metric span2">
            <div className="k">Duration</div>
            <div className="v mono">{isCharging ? helper_fmtTime(data?.latestSession?.metrics?.durationSeconds) : '--'}</div>
          </div>
        </div>
      </div>

      {data?.latestSession ? (
        <div className="gun-meta">
          <div className="meta-row">
            <span className="meta-k">Phiên gần nhất</span>
            <span className="meta-v mono">{data?.connector?.status === 'Charging' ? 'Đang sạc' : 'Đã kết thúc'}</span>
          </div>
          {data?.connector?.errorCode !== 'NoError' ? (
            <div className="meta-row meta-error">
              <span className="meta-k">Error</span>
              <span className="meta-v mono">{data?.connector?.errorCode}</span>
            </div>
          ) : null}
          <InlineGrid gap="400" columns={{ xs: 1, sm: 1, md: ['oneHalf', 'oneHalf'], lg: ['oneHalf', 'oneHalf'] }}>
            <Box>
              <Player autoplay loop={data?.connector?.status === 'Charging'} renderer="svg" src={evCharging} style={{ width: 140, height: 140 }} />
            </Box>
            <BlockStack gap="300">
              <DescriptionList
                gap="tight"
                items={[
                  { term: 'Tag ID', description: data?.latestSession?.vehicle?.tagId ?? '-' },
                  { term: 'Biển số', description: data?.latestSession?.vehicle?.licensePlate ?? '-' },
                  { term: 'Thời gian sạc', description: helper_fmtTime(data?.latestSession?.metrics?.durationSeconds) },
                  {
                    term: 'Mã phiên',
                    description: (
                      <Link
                        onClick={() => {
                          history(`/charge_transaction/view/${data?.latestSession?.id}`);
                        }}
                      >
                        {data?.latestSession?.ocppTransactionId}
                      </Link>
                    ),
                  },
                ]}
              />
            </BlockStack>
          </InlineGrid>

          {!isCharging ? (
            <div>
              <InlineStack align="space-between" blockAlign="center">
                <Box minWidth="100px">
                  <Text as="p">Soc</Text>
                  <Text as="h3" variant="headingXl">
                    {Number(data?.latestSession?.metrics?.socEnd || '0')} %
                  </Text>
                </Box>
                <Box minWidth="100px">
                  <Text as="p">Energy</Text>
                  <Text as="h3" variant="headingXl">
                    {helper_fmtNumber(data?.latestSession?.metrics?.energyKwh, 1)} Kw
                  </Text>
                </Box>
                <Box minWidth="100px">
                  <Text as="p">Power</Text>
                  <Text as="h3" variant="headingXl">
                    {helper_fmtNumber(data?.latestSession?.metrics?.powerKw, 1)} Kw
                  </Text>
                </Box>
              </InlineStack>
              <InlineStack align="space-between" blockAlign="center">
                <Box minWidth="100px">
                  <Text as="p">Voltage</Text>
                  <p>{helper_fmtNumber(data?.latestSession?.metrics?.voltageV, 0)} V</p>
                </Box>

                <Box minWidth="100px">
                  <Text as="p">Current</Text>
                  <p>{helper_fmtNumber(data?.latestSession?.metrics?.currentA, 0)} A</p>
                </Box>
                <Box minWidth="100px">
                  <Text as="p">Duration</Text>
                  <p>{helper_fmtTime(data?.latestSession?.metrics?.durationSeconds)}</p>
                </Box>
              </InlineStack>
            </div>
          ) : (
            <Button icon={StopCircleIcon} variant="primary" tone="critical" onClick={() => onEnd()} disabled={!canEnd}>
              Dừng phiên sạc
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}

function StationVisual({ title }: { title: string }) {
  return (
    <div className="station-wrap station-wrap--cute">
      <div className="station-cartoon" aria-label="Cute charging station">
        <Player autoplay src={evChargerStation} loop={false} />

        <div className="station-label">
          <div className="station-label-sub muted">{title || 'Dual Gun Station'}</div>
        </div>
      </div>

      <div className="cables">
        <div className="cable left" />
        <div className="cable right" />
      </div>
    </div>
  );
}

function ApprovalModal({
  req,
  busy,
  onResolve,
}: {
  req: ApprovalRequest;
  busy: boolean;
  onResolve: (decision: ApprovalDecision, remember: boolean) => void;
}) {
  return (
    <div className="approval-backdrop" role="dialog" aria-modal="true">
      <div className="approval-modal">
        <div className="approval-head">
          <div className="approval-title">Yêu cầu xác nhận sạc</div>
          <div className="approval-sub mono">approvalId: {req.approvalId}</div>
        </div>

        <div className="approval-body">
          <div className="row">
            <div className="k">CPID</div>
            <div className="v mono">{req.cpid}</div>
          </div>
          <div className="row">
            <div className="k">Connector</div>
            <div className="v mono">{req.connectorId}</div>
          </div>
          <div className="row">
            <div className="k">idTag</div>
            <div className="v mono">{req.idTag || '—'}</div>
          </div>
          <div className="row">
            <div className="k">At</div>
            <div className="v mono">{req.at}</div>
          </div>
          {req.expiresAt ? (
            <div className="row">
              <div className="k">Expires</div>
              <div className="v mono">{req.expiresAt}</div>
            </div>
          ) : null}
        </div>

        <div className="approval-actions">
          <button className="btn btn-end" disabled={busy} onClick={() => onResolve('deny', false)}>
            Từ chối
          </button>
          <button className="btn btn-start" disabled={busy} onClick={() => onResolve('allow', false)}>
            Đồng ý
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChargingKiosk({ station, connectors = [], stationName, stationId, vendor, model }: Props) {
  const { mutateAsync: startTransactionAsGuest } = useStartTransaction();
  const { mutateAsync: stopTransaction } = useStopTransaction();

  const resolvedStationName = station?.name ?? stationName ?? '—';
  const resolvedStationId = station?.cpid ?? stationId ?? '';
  const isOnline = Boolean(station?.onlineStatus);
  const hotline = station?.hotline;
  const address = station?.address;

  const stationIdRaw = useMemo(() => String(resolvedStationId || '').trim(), [resolvedStationId]);
  const stationIdNorm = useMemo(() => stationIdRaw.toLowerCase(), [stationIdRaw]);

  // effective cpid resolved from incoming socket snapshots (fallback if prop mismatches case/spaces)
  const [effectiveCpid, setEffectiveCpid] = useState<string>(stationIdRaw);
  const effectiveCpidRef = useRef<string>(stationIdRaw);
  useEffect(() => {
    setEffectiveCpid(stationIdRaw);
  }, [stationIdRaw]);
  useEffect(() => {
    effectiveCpidRef.current = effectiveCpid;
  }, [effectiveCpid]);

  const [left, setLeft] = useState<ConnectorWithLatestSessionDTO>(null);
  const [right, setRight] = useState<ConnectorWithLatestSessionDTO>(null);

  const [approvalQueue, setApprovalQueue] = useState<ApprovalRequest[]>([]);
  const [approvalBusy, setApprovalBusy] = useState(false);

  const updateConnectorStatus = (connectorId: number, status: keyof typeof ConnectorStatus) => {
    const updateEntry = (prev: ConnectorWithLatestSessionDTO | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        connector: {
          ...prev.connector,
          status,
          statusTimestamp: new Date().toISOString(),
        },
      };
    };

    if (connectorId === 1) {
      setLeft(updateEntry);
      return;
    }
    if (connectorId === 2) {
      setRight(updateEntry);
    }
  };

  const connectorsById = useMemo(() => {
    const map = new Map<number, ConnectorWithLatestSessionDTO[][number]>();
    for (const entry of connectors) {
      map.set(entry.connector.connectorId, entry);
    }
    return map;
  }, [connectors]);

  useEffect(() => {
    if (!connectors.length) return;

    const leftEntry = connectorsById.get(1);
    const rightEntry = connectorsById.get(2);

    setLeft(leftEntry || null);
    setRight(rightEntry || null);
  }, [connectors, connectorsById, isOnline]);

  const getGunByConnector = (connectorId: number) => (connectorId === 1 ? left : right);

  useEffect(() => {
    const onApprovalRequest = (req: any) => {
      try {
        const approvalId = String(req?.approvalId || '');
        if (!approvalId) return;

        const normalized: ApprovalRequest = {
          approvalId,
          cpid: String(req?.cpid || req?.stationId || ''),
          idTag: String(req?.idTag || ''),
          connectorId: Number(req?.connectorId || 0),
          at: String(req?.at || ''),
          expiresAt: req?.expiresAt ? String(req.expiresAt) : undefined,
        };

        setApprovalQueue((q) => {
          if (q.some((x) => x.approvalId === approvalId)) return q;
          return [...q, normalized];
        });
      } catch {
        // ignore
      }
    };

    const clearApproval = (approvalIdRaw: any) => {
      const approvalId = String(approvalIdRaw || '');
      if (!approvalId) return;
      setApprovalQueue((q) => q.filter((x) => x.approvalId !== approvalId));
    };

    const onApprovalResolved = (payload: any) => clearApproval(payload?.approvalId);
    const onApprovalTimeout = (payload: any) => clearApproval(payload?.approvalId);

    socket.on('approval:request', onApprovalRequest);
    socket.on('approval:resolved', onApprovalResolved);
    socket.on('approval:timeout', onApprovalTimeout);

    return () => {
      socket.off('approval:request', onApprovalRequest);
      socket.off('approval:resolved', onApprovalResolved);
      socket.off('approval:timeout', onApprovalTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationIdNorm]);

  const start = async (connectorId: number) => {
    const gun = getGunByConnector(connectorId);
    if (gun.connector.status !== ConnectorStatus.Preparing) {
      alert('ERR_3994');
      return;
    }

    try {
      updateConnectorStatus(connectorId, ConnectorStatus.Charging);
      await startTransactionAsGuest({
        cpid: effectiveCpidRef.current || resolvedStationId,
        connectorId,
        idTag: null, // nếu backend set DEFAULT_REMOTE_IDTAG thì vẫn chạy
      });
      // KHÔNG set demo Charging — chờ StatusNotification/MeterValues thật bắn về
    } catch (e: any) {
      updateConnectorStatus(connectorId, gun.connector.status);
      console.error(e, 'START_TRANSACTION_ERROR');
    }
  };

  const stop = async (connectorId: number) => {
    const gun = getGunByConnector(connectorId);
    if (gun.connector.status !== ConnectorStatus.Charging) return;
    if (!isLive) {
      return;
    }

    try {
      updateConnectorStatus(connectorId, ConnectorStatus.Finishing);
      await stopTransaction({
        cpid: effectiveCpidRef.current || resolvedStationId,
        connectorId,
      });
      updateConnectorStatus(connectorId, ConnectorStatus.Available);
    } catch (e: any) {
      updateConnectorStatus(connectorId, ConnectorStatus.Charging);
      console.error(e, 'STOP_TRANSACTION_ERROR');
    }
  };

  const isLive = isOnline;

  const { addNotification } = useNotification();

  return (
    <div className="kiosk">
      <header className="topbar">
        <div className="brand">
          <div className="logo">J</div>
          <div className="brand-text">
            <div className="title">{resolvedStationName}</div>
            <div className="sub">
              Station ID: <span className="mono">{stationIdRaw}</span>
              {normId(effectiveCpid) !== stationIdNorm ? (
                <span className="muted">
                  {' '}
                  (socket: <span className="mono">{effectiveCpid}</span>)
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="top-right">
          <div
            className={`top-chip ${isLive ? '' : 'subtle'}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            {isLive ? <span className="dot live">&nbsp;</span> : <span className="dot">&nbsp;</span>}
            Live Status
          </div>
        </div>
      </header>

      <main className="stage">
        <GunCard data={left} isLive={isLive} onStart={() => start(1)} onEnd={() => stop(1)} />
        <StationVisual title={resolvedStationName} />

        <GunCard data={right} isLive={isLive} onStart={() => start(2)} onEnd={() => stop(2)} />
      </main>

      {approvalQueue.length > 0 ? (
        <ApprovalModal
          req={approvalQueue[0]}
          busy={approvalBusy}
          onResolve={async (decision, remember = false) => {
            const cur = approvalQueue[0];
            if (!cur) return;

            setApprovalBusy(true);
            try {
              await new Promise<void>((resolve, reject) => {
                const t = setTimeout(() => reject(new Error('approval_ack_timeout')), 10000);
                socket.emit('approval:resolve', { approvalId: cur.approvalId, decision, remember }, (ack: any) => {
                  clearTimeout(t);
                  if (ack?.ok === false) return reject(new Error(String(ack?.message || 'approval_failed')));
                  resolve();
                });
              });
              addNotification('info', 'Đã duyệt, phiên sẽ bắt đầu ngay!');
            } catch (e) {
              // nếu fail thì giữ lại để bấm lại
              console.error('[approval] resolve failed:', e);
              addNotification('error', 'Không duyệt được phiên này, có lỗi xảy ra hoặc đã hết hạn duyệt!');
            } finally {
              setApprovalBusy(false);
              setApprovalQueue((q) => q.filter((x) => x.approvalId !== cur.approvalId));
            }
          }}
        />
      ) : null}
      <footer className="footer">
        <div className="footer-left">
          <span className={`dot ${isLive ? 'live' : ''}`} />
          <span>{isLive ? 'Live data' : 'Waiting data'}</span>
          <span className="sep" />
          <span className="muted">{hotline || 'Hotline liên hệ.'}</span>

          <span className="sep" />
          <span className="muted">{address || 'Địa chỉ của cây sạc.'}</span>
        </div>
        <div className="footer-right mono">{new Date().toLocaleTimeString()}</div>
      </footer>
    </div>
  );
}

